import {
  ConsentDocument,
  ConsentDocumentActiveStage,
  ConsentDocumentStatus,
  ConsentSignerRole,
  ConsentSignatoryState,
} from "@source/api/models/consentModels";
import {getConsentRequestStringCode, usesRoleBasedEmployeeSigners} from "@source/api/models/consentHelpers";
import {Role} from "@source/utils/models/adminModels";

export interface EmployeeSignAccess {
  /** The awaiting-signature queue or employee document endpoint already authorized this person. */
  queueAuthorized?: boolean;
  role?: string;
  homeIds?: string[];
}

export const employeeSignAccessFromUser = (
  user: {account?: {role?: string; nursingHomes?: Array<{_id: string}>; permissions?: {homes?: Array<{home?: {_id?: string}}>}}} | null | undefined,
  queueAuthorized = false,
): EmployeeSignAccess => ({
  queueAuthorized,
  role: user?.account?.role,
  homeIds: [
    ...(user?.account?.nursingHomes?.map((home) => home._id) || []),
    ...(user?.account?.permissions?.homes?.map((permission) => permission.home?._id || "") || []),
  ].filter(Boolean),
});

const canSignRoleBasedEmployeeSlot = (document: ConsentDocument, access?: EmployeeSignAccess) => {
  const roles = document.categorySnapshot?.employeeSignerRoles?.filter(Boolean) ?? [];
  if (roles.length) {
    if (!access?.role || !roles.includes(access.role)) return false;
  } else if (!access?.queueAuthorized) {
    return false;
  }
  if (access?.queueAuthorized || access?.role === Role.ORGANIZATION_ADMIN) return true;
  const homeId = document.nursingHome?._id;
  return Boolean(homeId && access?.homeIds?.includes(homeId));
};

const openEmployeeSignatory = (document: ConsentDocument) =>
  document.signatories?.find((signatory) =>
    signatory.role === ConsentSignerRole.EMPLOYEE && !signatory.id);

export const filterAwaitingSignDocuments = (
  documents: ConsentDocument[],
  search: string,
  dateRange: [Date, Date] | [Date] | null,
) => {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const start = dateRange?.[0] ? new Date(dateRange[0].getFullYear(), dateRange[0].getMonth(), dateRange[0].getDate()).getTime() : undefined;
  const endDate = dateRange?.[1] || dateRange?.[0];
  const end = endDate ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).getTime() : undefined;

  return documents.filter((document) => {
    const searchable = [
      document.title,
      document.resident?.firstName,
      document.resident?.lastName,
      document.categorySnapshot?.name,
      document.nursingHome?.name,
    ].filter(Boolean).join(" ").toLocaleLowerCase();
    const sentAt = document.sentAt ? new Date(document.sentAt).getTime() : NaN;
    return (!normalizedSearch || searchable.includes(normalizedSearch))
      && (start === undefined || (!Number.isNaN(sentAt) && sentAt >= start))
      && (end === undefined || (!Number.isNaN(sentAt) && sentAt <= end));
  });
};

export const canCurrentEmployeeSign = (
  document: ConsentDocument,
  employeeId: string,
  access?: EmployeeSignAccess,
) => {
  if (
    !employeeId
    || document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
  ) return false;

  const employeeStage = document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES);
  const clinicalStage = document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE);
  if (!employeeStage && !clinicalStage) return false;

  if (document.signatories !== undefined) {
    return document.signatories.some((signatory) => {
      if (signatory.state !== ConsentSignatoryState.ACTIVE || signatory.signed) return false;
      const assignedToEmployee = signatory.id === employeeId
        && ((employeeStage && signatory.role === ConsentSignerRole.EMPLOYEE)
          || (clinicalStage && signatory.role === ConsentSignerRole.GP_CLINICAL));
      const openEmployeeSlot = employeeStage
        && signatory.role === ConsentSignerRole.EMPLOYEE
        && !signatory.id
        && canSignRoleBasedEmployeeSlot(document, access);
      return assignedToEmployee || openEmployeeSlot;
    });
  }

  const hasSigned = document.signatures?.some((signature) => signature.signerId === employeeId);
  const isRequiredEmployee = employeeStage && document.requiredEmployeeSigners?.some((employee) => employee._id === employeeId);
  const isRoleBasedEmployee = employeeStage
    && !hasSigned
    && usesRoleBasedEmployeeSigners(document.categorySnapshot)
    && canSignRoleBasedEmployeeSlot(document, access);
  const isClinicalSigner = clinicalStage && document.clinicalSigner?._id === employeeId
    && !document.signatures?.some((signature) => signature.signerType === ConsentSignerRole.GP_CLINICAL);
  return Boolean((isRequiredEmployee && !hasSigned) || isRoleBasedEmployee || isClinicalSigner);
};

export const isRoleBasedEmployeeStageOpen = (document: ConsentDocument) => {
  const signatory = openEmployeeSignatory(document);
  return Array.isArray(document.activeStages)
    && document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES)
    && Boolean(signatory && signatory.state === ConsentSignatoryState.ACTIVE && !signatory.signed);
};

/** The open role slot is this person's signature, so the employee statement applies. */
export const isEmployeeRoleSignature = (document: ConsentDocument, access?: EmployeeSignAccess) =>
  isRoleBasedEmployeeStageOpen(document) && canSignRoleBasedEmployeeSlot(document, access);

const alreadySignedByAnotherEmployee = (error: unknown) => {
  const code = getConsentRequestStringCode(error);
  return code === "consent_document_wrong_signer" || code === "consent_document_invalid_state_transition";
};

export const getEmployeeSignErrorMessage = (error: unknown) => {
  if (alreadySignedByAnotherEmployee(error)) return "This document has already been signed by another employee.";
  const status = typeof error === "object" && error !== null && "status" in error
    ? (error as {status?: number}).status
    : undefined;
  if (status === 403) return "You are no longer authorized to review or sign this document.";
  if (status === 404) return "This consent document is no longer available.";
  if (status === 409) return "This document has already been signed or its signing stage has advanced.";
  if (status === 400) return "Your acknowledgement could not be accepted. Review the document and try again.";
  return "Unable to complete your signature. Please try again.";
};

export const employeeSignAttemptEnded = (error: unknown) => {
  if (alreadySignedByAnotherEmployee(error)) return true;
  const status = typeof error === "object" && error !== null && "status" in error
    ? (error as {status?: number}).status
    : undefined;
  return status === 403 || status === 404 || status === 409;
};

export const getEmployeeSignSuccessTitle = (status: ConsentDocumentStatus) =>
  status === ConsentDocumentStatus.COMPLETED
    ? "Document completed"
    : "You signed · Waiting for other required signers";
